// src/components/themes/default/admin/BranchLogin.tsx
"use client";

import { Building2, Lock, Mail, Eye, EyeOff, Loader2, Sparkles, ShieldCheck } from "lucide-react";

interface BranchLoginProps {
  branchSlug: string;
  branchName?: string;
  email: string;
  setEmail: (val: string) => void;
  password: string;
  setPassword: (val: string) => void;
  showPassword: boolean;
  setShowPassword: (val: boolean) => void;
  loading: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  primaryColor?: string;
}

export default function DefaultBranchLogin({
  branchSlug,
  branchName,
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  loading,
  error,
  onSubmit,
  primaryColor = "#059669",
}: BranchLoginProps) {
  return (
    <div className="fixed inset-0 z-[99999] w-screen h-screen bg-slate-950 text-white flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 my-auto">
        <div className="text-center space-y-2">
          <div
            style={{ backgroundColor: `${primaryColor}20`, borderColor: `${primaryColor}40` }}
            className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto border"
          >
            <Building2 style={{ color: primaryColor }} className="w-6 h-6" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-black uppercase tracking-wider">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Modern Outlet Portal</span>
          </div>
          <h1 className="text-xl font-black capitalize tracking-tight mt-1">
            {branchName || branchSlug.replace(/-/g, " ")}
          </h1>
          <p className="text-xs text-slate-400">
            শাখা ড্যাশবোর্ডে প্রবেশ করতে ম্যানেজার ক্রেডেনশিয়াল দিন
          </p>
        </div>

        {error && (
          <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold leading-relaxed">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
              ম্যানেজার ইমেইল
            </label>
            <div className="relative flex items-center">
              <Mail className="w-4 h-4 absolute left-3.5 text-slate-500" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="branch.manager@gmail.com"
                className="w-full pl-10 pr-4 py-3 rounded-2xl text-xs font-bold bg-slate-950/60 border border-slate-800 text-white outline-none focus:border-emerald-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
              পাসওয়ার্ড
            </label>
            <div className="relative flex items-center">
              <Lock className="w-4 h-4 absolute left-3.5 text-slate-500" />
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="লগইন পাসওয়ার্ড লিখুন"
                className="w-full pl-10 pr-10 py-3 rounded-2xl text-xs font-bold bg-slate-950/60 border border-slate-800 text-white outline-none focus:border-emerald-500 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 text-slate-500 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{ backgroundColor: primaryColor }}
            className="w-full py-3.5 rounded-2xl text-xs font-black text-white shadow-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>যাচাই হচ্ছে...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                <span>ব্রাঞ্চে প্রবেশ করুন</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}